import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInCalendarDays,
    endOfMonth,
    format,
    isSameDay,
    isSameMonth,
    isWeekend as fnsIsWeekend,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import type { Task, CustomField } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { GanttRow } from './gantt-row';
import {
    TIME_SCALE_PX_PER_UNIT,
    TIME_SCALE_PX_PER_DAY,
    buildGroups,
    groupOf,
    resolveTaskRange,
    type GanttGroupBy,
    type GanttTimeScale,
    type TaskRangeResolution,
} from './gantt-config';
import { getSprintBands, type SprintDef } from '@/hooks/use-gantt';
import { useTaskDependencies, type TaskDependency } from '@/hooks/use-task-dependencies';

// ============================================
// LAYOUT CONSTANTS (t14)
// ============================================
//
// Pixel heights of rows and the group header. These are mirrored in
// gantt-row.tsx — every row uses h-12 (48px).
// (36px), and the group header is ~28px. We hardcode them here so
// the dependency-arrow SVG overlay can compute Y-coordinates
// without querying the DOM.

const PARENT_ROW_HEIGHT = 48;
// R9 / t36: subtask row variant removed — every row is h-12
// (= 48px = PARENT_ROW_HEIGHT). The previous `SUBTASK_ROW_HEIGHT = 36`
// constant is gone.
const GROUP_HEADER_HEIGHT = 28;

// R8 / t34: helper used by the arrow-anchor computation to read
// the *actual* DOM bounding box of a bar (tagged with
// `data-gantt-bar` in GanttBar) and convert it from viewport
// coordinates into the SVG overlay's local coordinate system.
// Returns null when the bar hasn't mounted yet (e.g. the user
// expanded a subtask mid-render). We deliberately take both rects
// in the same render pass so the SVG's offset is consistent with
// the bar's position — if the user scrolls between two renders
// both rects shift by the same amount, so the SVG-local
// coordinates stay aligned.
//
// R9 / t36: also returns `top` and `bottom` so the above-route
// path can compute its end-Y as `targetTop - 4` (dropping down
// onto the top edge of the target bar, arrowhead pointing
// downward) instead of the previous "end at the bar's left edge
// vertical-middle" — which drew the arrowhead on the side of
// the target rather than landing on top of it.
function getBarSvgCoords(
    taskId: string,
    svgEl: SVGSVGElement | null
): { left: number; right: number; middleY: number; top: number; bottom: number } | null {
    if (!svgEl || typeof document === 'undefined') return null;
    const barEl = document.querySelector(
        `[data-gantt-bar="${CSS.escape(taskId)}"]`
    ) as HTMLElement | null;
    if (!barEl) return null;
    const barRect = barEl.getBoundingClientRect();
    const svgRect = svgEl.getBoundingClientRect();
    return {
        left: barRect.left - svgRect.left,
        right: barRect.right - svgRect.left,
        middleY: barRect.top + barRect.height / 2 - svgRect.top,
        top: barRect.top - svgRect.top,
        bottom: barRect.bottom - svgRect.top,
    };
}

// ============================================
// GANTT TIMELINE
// ============================================
//
// t17: the actual scrollable grid. Renders:
//   1. A day/week/month header row
//   2. The grouped rows of tasks
//   3. Sprint band overlays when the project has a SPRINT field and
//      the user opted in to showSprints
//
// Layout is purely pixel-based: we compute one px/unit from the
// concrete time scale, then position each bar at
// `(daysFromOrigin) * pxPerDay` from the row's left edge. The
// timeline's total width = `numUnits * pxPerUnit`. The grid header
// shows the same width so columns line up.

// R3 / t17: shape of an in-progress drag's preview geometry. The
// parent (GanttView) computes the new start/end from the drag delta
// (mirroring handleDragEnd's logic) and passes the resulting Date
// pair down. GanttTimeline converts those into pixels using the
// same offsetForDate / widthForRange helpers so the math stays in
// one place. `taskId` lets the row identify which bar to override.
// `null` means "no drag in progress".
export interface GanttDragPreview {
    taskId: string;
    mode: 'move' | 'resize-start' | 'resize-end';
    previewStart: Date | null;
    previewEnd: Date | null;
}

export interface GanttTimelineProps {
    /** The first day visible in the timeline. */
    origin: Date;
    /** Concrete time scale (auto already resolved by the parent). */
    timeScale: Exclude<GanttTimeScale, 'auto'>;
    /** Group rows by status / assignee / none. */
    groupBy: GanttGroupBy;
    /** Whether to render sprint bands. */
    showSprints: boolean;
    /**
     * R3 / t17: whether to render tasks with no startDate AND no
     * endDate. When false (the new default) they are filtered out
     * entirely. When true they stay in the group, sorted LAST.
     */
    showDateless: boolean;
    /** Project's custom fields. */
    customFields: CustomField[];
    /**
     * R9 / t36: tasks are now root-only. The previous comment
     * said "INCLUDES both parent tasks AND subtasks" — that's no
     * longer true. We removed the chevron entirely so subtasks
     * are not rendered inside the Gantt at all.
     */
    tasks: Task[];
    /** Localised status label lookup (gantt.status.X). */
    statusLabel: (status: Task['status']) => string;
    /** Localised unassigned label. */
    unassignedLabel: string;
    /** True if the user can drag bars. */
    canDrag: boolean;
    onTaskClick?: (task: Task) => void;
    /**
     * R3 / t17: live drag preview geometry. While the user is
     * dragging a bar's move / resize handle this carries the
     * post-delta start/end; null when no drag is active. GanttRow
     * checks taskId equality to know whether to use the preview
     * geometry for its bar.
     */
    dragPreview: GanttDragPreview | null;
    // R4 / t22: cursor-driven dep picker state, threaded to each
    // GanttRow so it can:
    //   - right-click with the new "Add a dependency on this
    //     task…" single-option menu.
    //   - highlight the pick-source task row.
    pickState: 'idle' | 'pickingTarget';
    pickSourceTaskId: string | null;
    pickCursorPos: { x: number; y: number } | null;
    /** R6 / t31: kept for back-compat with the older prop
     * signature but ignored — every dep is now FS. The
     * returned callback just enters pick mode for the named
     * task. */
    onStartPickTarget: (direction: 'outgoing' | 'incoming') => (taskId: string) => void;
}

interface UnitCell {
    label: string;
    subLabel?: string;
    date: Date;
    isWeekend: boolean;
    isToday: boolean;
    /** Optional sprint band rendered behind this cell. */
    sprintBands: Array<{ sprint: SprintDef; isStart: boolean; isEnd: boolean }>;
}

function buildUnits(
    origin: Date,
    timeScale: Exclude<GanttTimeScale, 'auto'>,
    sprints: SprintDef[],
    unitCount?: number
): UnitCell[] {
    const units: UnitCell[] = [];
    const today = new Date();

    if (timeScale === 'day') {
        // R12 / t40: the day view used to be a fixed 28-day window
        // regardless of where the tasks landed. That made the
        // timeline extend "all the way to the right" with no day
        // labels once a task landed past day 28, AND clipped any
        // arrow whose source or target bar lived past day 28.
        // Now `unitCount` is supplied by the caller (default 28)
        // and grows to cover the rightmost task with a small
        // trailing buffer so the SVG overlay can paint the arrow.
        const days = unitCount ?? 28;
        for (let i = 0; i < days; i++) {
            const d = addDays(origin, i);
            units.push({
                label: format(d, 'd'),
                subLabel: format(d, 'EEE'),
                date: d,
                isWeekend: fnsIsWeekend(d),
                isToday: isSameDay(d, today),
                sprintBands: sprintBandsForDay(d, sprints),
            });
        }
    } else if (timeScale === 'week') {
        // 12 weeks from origin (capped at start-of-week).
        const weeks = unitCount ?? 12;
        for (let i = 0; i < weeks; i++) {
            const weekStart = addWeeks(origin, i);
            const ws = startOfWeek(weekStart, { weekStartsOn: 1 });
            units.push({
                label: format(ws, "MMM d"),
                subLabel: `W${format(ws, 'II')}`,
                date: ws,
                isWeekend: false,
                isToday:
                    today >= ws && today < addWeeks(ws, 1),
                sprintBands: sprintBandsForDay(ws, sprints),
            });
        }
    } else {
        // month — 6 months from origin (anchored to month-start).
        const months = unitCount ?? 6;
        const mStart = startOfMonth(origin);
        for (let i = 0; i < months; i++) {
            const monthStart = addMonths(mStart, i);
            units.push({
                label: format(monthStart, 'MMM'),
                subLabel: format(monthStart, 'yyyy'),
                date: monthStart,
                isWeekend: false,
                isToday: isSameMonth(monthStart, today),
                sprintBands: sprintBandsForDay(monthStart, sprints),
            });
        }
    }

    return units;
}

function sprintBandsForDay(
    day: Date,
    sprints: SprintDef[]
): UnitCell['sprintBands'] {
    const out: UnitCell['sprintBands'] = [];
    for (const sprint of sprints) {
        const sd = new Date(sprint.startDate);
        const ed = new Date(sprint.endDate);
        if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) continue;
        if (day >= sd && day <= ed) {
            // Determine if this is the first or last unit overlapping
            // the sprint — used to round the band's edges so the
            // visible band looks like one continuous shape.
            const prev = addDays(day, -1);
            out.push({
                sprint,
                isStart: prev < sd,
                isEnd: addDays(day, 1) > ed,
            });
        }
    }
    return out;
}

export function GanttTimeline({
    origin,
    timeScale,
    groupBy,
    showSprints,
    showDateless,
    customFields,
    tasks,
    statusLabel,
    unassignedLabel,
    canDrag,
    onTaskClick,
    dragPreview,
    pickState,
    pickSourceTaskId,
    pickCursorPos,
    // R6: pickDirection is no longer used (every dep is FS).
    // Kept off the destructure list to avoid the unused-var lint.
    onStartPickTarget,
}: GanttTimelineProps) {
    const { t } = useI18n();

    const { field: sprintField, sprints } = useMemo(
        () => getSprintBands(customFields),
        [customFields]
    );

    const pxPerUnit = TIME_SCALE_PX_PER_UNIT[timeScale];
    // R2 fix: bar position / width live in day-relative pixels, not
    // unit-relative pixels. At week scale, the grid cell is 120px
    // wide (one week) but a single day is only 120/7 ≈ 17px — so a
    // 5-day task should live at ~85px from origin and span ~85px,
    // not jump 5 * 120 = 600px (= 5 weeks) off-screen. The grid
    // header stays at pxPerUnit so columns still line up.
    const pxPerDay = TIME_SCALE_PX_PER_DAY[timeScale];
    // R12 / t40: compute the rightmost task extent up front so the
    // timeline width and the SVG overlay both grow to cover it.
    // Without this, a task that lives >28 days past the origin in
    // day view (or >12 weeks in week view) had its bar clipped by
    // the row's `overflow-hidden`, and any arrow into / out of it
    // was clipped by the SVG viewport.
    const maxBarExtentPx = useMemo(() => {
        let maxRight = 0;
        for (const task of tasks) {
            const range = resolveTaskRange(task);
            if (!range.hasAnyDate || range.end == null) continue;
            const right =
                differenceInCalendarDays(range.end, origin) * pxPerDay + pxPerDay;
            if (right > maxRight) maxRight = right;
        }
        return maxRight;
    }, [tasks, origin, pxPerDay]);
    // Trailing buffer (~2 days at day scale, ~1 unit at week/month)
    // so the LAST bar's arrowhead + a small empty strip both sit
    // inside the SVG.
    const TIMELINE_TRAIL_BUFFER_PX = pxPerUnit;
    const requiredUnits = Math.ceil(
        (maxBarExtentPx + TIMELINE_TRAIL_BUFFER_PX) / pxPerUnit
    );
    // R12 / t40: default window sizes preserved for projects that
    // DON'T have any task past the origin — keeps the "compact"
    // view users are used to.
    const DEFAULT_UNITS: Record<Exclude<GanttTimeScale, 'auto'>, number> = {
        day: 28,
        week: 12,
        month: 6,
    };
    const unitCount = Math.max(DEFAULT_UNITS[timeScale], requiredUnits);
    const units = useMemo(
        () => buildUnits(origin, timeScale, sprints, unitCount),
        [origin, timeScale, sprints, unitCount]
    );
    const totalWidth = units.length * pxPerUnit;

    // t14: project dependency edges — fetched at the timeline level
    // so the SVG overlay (drawn on top of every row) can resolve
    // (predecessor taskId → bar geometry) for every arrow.
    const projectId = tasks[0]?.projectId;
    const { data: dependenciesData } = useTaskDependencies(projectId);
    const dependencies = dependenciesData?.dependencies || [];

    // R8 / t34: ref to the SVG overlay + a tick state. We re-read
    // every bar's `getBoundingClientRect()` after the SVG mounts so
    // the arrow anchors land on the bar's *actual* vertical middle
    // (the user spec calls for the bar's center, not the row's
    // center). `setSvgMountTick` is called from the SVG's callback
    // ref the moment the SVG is in the DOM, which then re-runs the
    // arrows useMemo with a live svgRef.
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [svgMountTick, setSvgMountTick] = useState(0);
    const svgRefCallback = useCallback(
        (el: SVGSVGElement | null) => {
            svgRef.current = el;
            if (el) {
                setSvgMountTick((n) => n + 1);
            }
        },
        []
    );

    // R10 / t38: the arrows useMemo reads DOM coords via
    // `getBarSvgCoords`, which calls `getBoundingClientRect()` on
    // each `[data-gantt-bar]` element. When the time scale changes,
    // the bars re-render with new `left` / `width` (driven by the
    // new `pxPerDay`), but the React render that flips `timeScale`
    // and the browser's DOM commit happen in different microtasks —
    // so the FIRST arrows useMemo call after a time-scale switch
    // reads the OLD bar positions. The user feedback was:
    // "when we click on 'Days', 'Week' etc to change the type of
    // vision we have, then the arrows are bugged and we need to
    // click again on the button for the arrows to refresh".
    //
    // R11 / t39: same root cause applies whenever ANY of the
    // inputs that drives bar layout changes — a filter toggle, a
    // task add/delete, a dep add/delete — but `barGeometryByTaskId`
    // and `rowYByTaskId` only re-compute on the JSX render pass,
    // while `getBarSvgCoords` reads the DOM after commit. The user
    // reported: "When we change a filter, add a task, etc, it's not
    // updating the gantt unless we move or resize or change the
    // view".
    //
    // Fix: bump `recomputeTick` from a `useEffect` that fires AFTER
    // the DOM commit. `requestAnimationFrame` is enough to push the
    // increment past the React commit; we add the tick to the
    // arrows useMemo deps so it forces a second pass that reads
    // the now-up-to-date DOM. The state + effect are declared after
    // `visibleParentTasks` so the deps can be derived here.
    const [recomputeTick, setRecomputeTick] = useState(0);

    // Days from origin to a given date, clamped to the visible window.
    function offsetForDate(d: Date | null): number {
        if (!d) return 0;
        const days = differenceInCalendarDays(d, origin);
        return days * pxPerDay;
    }

    function widthForRange(range: TaskRangeResolution): number {
        if (!range.hasAnyDate) return 0;
        if (range.start == null || range.end == null || range.isPoint) return pxPerDay;
        const days = Math.max(
            1,
            differenceInCalendarDays(range.end, range.start) + 1
        );
        return days * pxPerDay;
    }

    // R9 / t36: subtasks are no longer rendered inside the Gantt —
// we keep only root tasks. The previous "split into parents /
// children" logic (and the `subtasksByParentId` merge) is gone.
// The bucketing loop now just filters out anything with a
// non-null parentId (defensive — the upstream loader should
// only return root tasks, but if it ever doesn't, those rows
// would otherwise sit at the top with their parentId
// dangling).
    const parentTasks = useMemo(() => {
        return tasks.filter((t) => !t.parentId);
    }, [tasks]);

    // R3 / t17: when showDateless is false, drop dateless parent
    // tasks entirely.
    const visibleParentTasks = useMemo(() => {
        if (showDateless) return parentTasks;
        return parentTasks.filter((t) => resolveTaskRange(t).hasAnyDate);
    }, [parentTasks, showDateless]);

    // Group rows for the active groupBy. Empty groups are still
    // rendered so the layout stays stable when a task moves between
    // groups.
    const groups = useMemo(
        () => buildGroups(visibleParentTasks, groupBy, statusLabel, unassignedLabel),
        [visibleParentTasks, groupBy, statusLabel, unassignedLabel]
    );

    // t14: dependency-arrow geometry. Walk the same rows the JSX
    // below will render, accumulating heights (parent = 48px,
    // subtask = 36px, group header = 28px). For each row we record
    // the (taskId, yCenter) so the SVG overlay can draw a line
    // from a predecessor's right edge → successor's left edge.
    //
    // Note: yCenter is RELATIVE to the inner content div (the one
    // with minWidth). The SVG overlay sits inside the same div so
    // its coordinate system matches.
    const rowYByTaskId = useMemo(() => {
        const map = new Map<string, number>();
        let y = 0;
        // Mirror the header row's height (sticky header). The header
        // doesn't have a fixed height in px — it's padding-driven
        // (py-2 + content). 40px is a safe estimate; if the arrow
        // starts/ends in the header zone we just clip it.
        const HEADER_HEIGHT = 40;
        y += HEADER_HEIGHT;

        for (const group of groups) {
            if (group.label) y += GROUP_HEADER_HEIGHT;
            const groupTasks = visibleParentTasks
                .filter((t) => groupOf(t, groupBy) === group.key)
                .sort((a, b) => {
                    const ra = resolveTaskRange(a);
                    const rb = resolveTaskRange(b);
                    const aHas = ra.hasAnyDate;
                    const bHas = rb.hasAnyDate;
                    if (aHas !== bHas) return aHas ? -1 : 1;
                    if (!aHas && !bHas) return a.id.localeCompare(b.id);
                    const aStart = ra.start?.getTime() ?? 0;
                    const bStart = rb.start?.getTime() ?? 0;
                    if (aStart !== bStart) return aStart - bStart;
                    const aEnd = ra.end?.getTime() ?? 0;
                    const bEnd = rb.end?.getTime() ?? 0;
                    if (aEnd !== bEnd) return aEnd - bEnd;
                    return a.id.localeCompare(b.id);
                });
            if (groupTasks.length === 0) continue;
            for (const task of groupTasks) {
                map.set(task.id, y + PARENT_ROW_HEIGHT / 2);
                y += PARENT_ROW_HEIGHT;
                // R9 / t36: subtask row expansion removed — every
                // row is now a parent row (h-12).
            }
        }
        return map;
    }, [groups, visibleParentTasks, groupBy]);

    // t14: per-row bar geometry map for arrow endpoints. Maps
    // taskId → { left, width } in timeline-local pixels (i.e. the
    // 0..totalWidth range, NOT including the 256px label column).
    // For dateless tasks we map left=0 and width=0 so the arrow
    // anchor lands at the timeline origin (the user will see a
    // short stub arrow, which is fine — there's nothing useful to
    // point at).
    const barGeometryByTaskId = useMemo(() => {
        const map = new Map<string, { left: number; width: number }>();
        for (const task of visibleParentTasks) {
            const range = resolveTaskRange(task);
            const left = offsetForDate(range.start);
            const width = range.hasAnyDate ? widthForRange(range) : 0;
            map.set(task.id, { left, width });
            // R9 / t36: subtask rows removed.
        }
        return map;
    }, [visibleParentTasks, origin, pxPerDay]);

    // R11 / t39: string fingerprint of (visibleParentTasks ids +
    // deps ids) so the effect fires when either list changes
    // content. We compare string equality (cheap) instead of
    // deep-equal.
    const visibleTasksKey = visibleParentTasks.map((t) => t.id).join(',');
    const dependenciesKey = dependencies.map((d) => d.id).join(',');
    useEffect(() => {
        // Skip the first render (the SVG-mount tick already
        // handles the very first paint). Only re-fire on
        // subsequent time-scale / filter / task-list / dep-list
        // changes.
        if (svgMountTick === 0) return;
        const raf = requestAnimationFrame(() => {
            setRecomputeTick((n) => n + 1);
        });
        return () => cancelAnimationFrame(raf);
    }, [timeScale, visibleTasksKey, dependenciesKey, svgMountTick]);

    // t14: SVG overlay paths for each dependency edge. We compute
    // R6 / t31: ALL dependency arrows now go LEFT-to-RIGHT only.
// The visual model is: the task on the LEFT is the
// predecessor, the task on the RIGHT is the successor. This
// collapses the four arrow types (FS / SS / FF / SF) into a
// single visual — every arrow starts at the vertical middle of
// the left task and lands at the vertical middle of the right
// task. The successor is the task that depends on (starts
// after) the predecessor, regardless of which dates align.
//
// We compute the canonical anchors by sorting the two endpoints
// of the edge by X position: leftmost is the source (the
// "predecessor" visually), rightmost is the target (the
// "successor" visually). All other type distinctions are
// dropped — every new dependency the picker creates is FS
// (successor starts after predecessor finishes) which matches
// the user's mental model: "the task I'm clicking on waits
// for the task I just picked".
    const arrows = useMemo(() => {
        return dependencies
            .map((dep) => {
                const pred = barGeometryByTaskId.get(dep.predecessorId);
                const succ = barGeometryByTaskId.get(dep.successorId);
                const predY = rowYByTaskId.get(dep.predecessorId);
                const succY = rowYByTaskId.get(dep.successorId);
                if (!pred || !succ || predY == null || succY == null) return null;

                // R13 / t41: the arrow ALWAYS goes from the
                // predecessor (the task that's blocking) to the
                // successor (the task that depends on it). The
                // previous R6 behaviour always routed the arrow
                // left-to-right and ended at the predecessor's
                // right edge when the successor sat to its left
                // — that's backwards from the user's mental
                // model. The arrowhead should always end at the
                // successor's LEFT edge so the user can see
                // which task is "waiting for" which.
                const predLeft = pred.left;
                const succRight = succ.left + succ.width;
                const succLeft = succ.left;
                const predRight = pred.left + pred.width;

                // `goingRight` describes the visual direction
                // the arrow travels across the timeline (left →
                // right when the successor sits to the right of
                // the predecessor, right → left when it sits to
                // the left). The routing logic uses it to pick
                // which way the polyline's first horizontal stub
                // should exit the source bar.
                const goingRight = succLeft >= predLeft;

                // R8 / t34: anchor coords come from the *bar's*
                // DOM bounding rect, not the row's vertical
                // middle. The bar is `top-1/2 -translate-y-1/2` in
                // a `h-12` row, so in theory the row middle equals
                // the bar middle — but the header / group-header
                // CSS line-heights and padding can drift a couple
                // of pixels off the `HEADER_HEIGHT = 40` /
                // `GROUP_HEADER_HEIGHT = 28` constants. The
                // rect-based read picks up whatever the browser
                // actually rendered so the arrow tip lands exactly
                // at the bar's vertical middle. We fall back to
                // the computed row middle when the SVG hasn't
                // mounted yet (first render, before the
                // setArrowTick effect runs).
                const svgEl = svgRef.current;
                const predBar = getBarSvgCoords(dep.predecessorId, svgEl);
                const succBar = getBarSvgCoords(dep.successorId, svgEl);
                const useDomCoords = !!predBar && !!succBar;
                // Source = predecessor's right edge (regardless
                // of visual direction).
                const sourceX = useDomCoords
                    ? predBar!.right
                    : predRight;
                const sourceY = useDomCoords
                    ? predBar!.middleY
                    : predY;
                // R9 / t36: capture the source's top so the
                // above-route can climb to an arc that's above BOTH
                // bars regardless of which is higher on the
                // timeline.
                const sourceTopY = useDomCoords
                    ? predBar!.top
                    : predY - PARENT_ROW_HEIGHT / 2;
                // Target = successor's left edge (regardless of
                // visual direction). The arrowhead lands at this
                // x — the path end is then offset 2px to the
                // LEFT of it in the SVG marker setup so the
                // tip extends just outside the bar's left
                // edge instead of overlapping the bar.
                const targetX = useDomCoords
                    ? succBar!.left
                    : succLeft;
                const targetY = useDomCoords
                    ? succBar!.middleY
                    : succY;
                // R9 / t36: the 'above' route drops onto the
                // top of the target (−4px) rather than into its
                // left edge so the arrowhead stays clear of the
                // bar's geometry.
                const targetTopY = useDomCoords
                    ? succBar!.top
                    : succY - PARENT_ROW_HEIGHT / 2;

                // R7 / t33: smart routing. When the horizontal
                // gap between the source and target endpoints is
                // tiny (bars overlap or sit on top of each other
                // on the timeline), routing a 90° polyline
                // through the bars would draw the line ON TOP of
                // the second bar — visually unreadable. Instead
                // we lift the arc ABOVE the source row and drop
                // it back down to the target's left edge so the
                // arrow stays clear of every bar it crosses.
                // Anything wider falls back to the previous
                // 90° polyline ("behind" routing).
                // R10 / t38: source/target Y-order drives the route.
                // The previous `gapX < 60` heuristic always lifted
                // an arc above BOTH bars regardless of which was
                // higher. When the source was already at the top
                // (sourceY < targetY) the arc drew an inverted-U
                // shape — up from the source, across the top,
                // down onto the target. The user feedback was:
                // "when the arrow is going down there is this
                // angle at the top where the arrow is first
                // going up then down".
                //
                // Rules (source = predecessor, target = successor):
                //   - same row         → straight line
                //   - source above     → simple 90° polyline (toward
                //                        target, down, toward target) — no arc
                //   - source below     → arc above both bars (up,
                //                        toward target, down to target top − 4)
                const sameRow = sourceY === targetY;
                const sourceAboveTarget = sourceY < targetY;
                const route: 'above' | 'down' | 'sameRow' = sameRow
                    ? 'sameRow'
                    : sourceAboveTarget
                    ? 'down'
                    : 'above';

                return {
                    id: dep.id,
                    type: 'FS' as const,
                    x1: sourceX,
                    y1: sourceY,
                    x2: targetX,
                    y2: targetY,
                    sourceTopY,
                    targetTopY,
                    goingRight,
                    route,
                } as const;
            })
            .filter((a): a is NonNullable<typeof a> => a !== null);
    }, [
        dependencies,
        barGeometryByTaskId,
        rowYByTaskId,
        svgMountTick,
        // R10 / t38: force the second pass after a time-scale
        // change so getBarSvgCoords reads the freshly committed
        // bar positions. See the recomputeTick useEffect above.
        recomputeTick,
    ]);

    return (
        <div className="rounded-lg border border-white/10 overflow-hidden bg-surface/30">
            <div className="overflow-x-auto">
                <div className="relative" style={{ minWidth: `${totalWidth + 256}px` }}>
                    {/* Header row */}
                    <div className="flex border-b border-white/10 bg-surface/50 sticky top-0 z-10">
                        <div className="w-64 shrink-0 px-2 sm:px-3 py-2 text-[10px] sm:text-xs font-medium uppercase tracking-wider text-foreground-muted border-r border-white/10">
                            {t('tasks.title')}
                        </div>
                        <div className="flex" style={{ width: `${totalWidth}px` }}>
                            {units.map((u, i) => {
                                const bgSprints =
                                    showSprints ? u.sprintBands : [];
                                return (
                                    <div
                                        key={i}
                                        className={cn(
                                            'relative shrink-0 border-r border-white/5 px-1 py-2 text-center',
                                            u.isWeekend ? 'bg-white/5' : '',
                                            u.isToday
                                                ? 'bg-primary/10'
                                                : ''
                                        )}
                                        style={{ width: `${pxPerUnit}px` }}
                                    >
                                        {/* Sprint band overlay (behind the label). */}
                                        {bgSprints.map((b, j) => (
                                            <div
                                                key={j}
                                                className="absolute inset-0 pointer-events-none"
                                                style={{
                                                    backgroundColor: `${
                                                        b.sprint.color ?? '#a78bfa'
                                                    }1A`, // ~10% alpha
                                                    borderTop: `2px solid ${
                                                        b.sprint.color ?? '#a78bfa'
                                                    }`,
                                                    borderTopLeftRadius: b.isStart ? 4 : 0,
                                                    borderTopRightRadius: b.isEnd ? 4 : 0,
                                                }}
                                                title={b.sprint.name}
                                            />
                                        ))}
                                        <div
                                            className={cn(
                                                'relative text-[11px] font-medium',
                                                u.isToday
                                                    ? 'text-primary'
                                                    : 'text-foreground'
                                            )}
                                        >
                                            {u.label}
                                        </div>
                                        {u.subLabel && (
                                            <div className="relative text-[10px] text-foreground-muted">
                                                {u.subLabel}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Group sections */}
                    {groups.map((group) => {
                        const groupTasks = visibleParentTasks
                            .filter((t) => groupOf(t, groupBy) === group.key)
                            .sort((a, b) => {
                                const ra = resolveTaskRange(a);
                                const rb = resolveTaskRange(b);
                                // R3 / t17: flip the R1 sort so dateless
                                // tasks go to the BOTTOM of their group
                                // instead of being pinned at the timeline
                                // origin. Dated tasks first (asc start
                                // date, asc end date, asc id), dateless
                                // tasks last (asc id for stability).
                                const aHas = ra.hasAnyDate;
                                const bHas = rb.hasAnyDate;
                                if (aHas !== bHas) return aHas ? -1 : 1;
                                if (!aHas && !bHas) {
                                    return a.id.localeCompare(b.id);
                                }
                                // Primary sort: startDate ascending.
                                const aStart = ra.start?.getTime() ?? 0;
                                const bStart = rb.start?.getTime() ?? 0;
                                if (aStart !== bStart) return aStart - bStart;
                                // Secondary sort: endDate ascending so a
                                // shorter bar with the same start sits
                                // to the left of a longer bar.
                                const aEnd = ra.end?.getTime() ?? 0;
                                const bEnd = rb.end?.getTime() ?? 0;
                                if (aEnd !== bEnd) return aEnd - bEnd;
                                return a.id.localeCompare(b.id);
                            });
                        return (
                            <div key={group.key} className="border-b border-white/5 last:border-b-0">
                                {/* Group header — only shown when the groupBy
                                    produces a meaningful label. */}
                                {group.label && (
                                    <div className="flex bg-surface/40 border-b border-white/5">
                                        <div className="w-64 shrink-0 px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-medium uppercase tracking-wider text-foreground-muted border-r border-white/5">
                                            {group.label}
                                        </div>
                                        <div
                                            className="flex-1"
                                            style={{ width: `${totalWidth}px` }}
                                        />
                                    </div>
                                )}
                                {groupTasks.length === 0 ? (
                                    <div className="flex">
                                        <div className="w-64 shrink-0 px-2 sm:px-3 py-2 sm:py-3 text-[10px] sm:text-xs text-foreground-muted border-r border-white/5 italic">
                                            —
                                        </div>
                                        <div
                                            className="flex-1"
                                            style={{ width: `${totalWidth}px` }}
                                        />
                                    </div>
                                ) : (
                                    groupTasks.map((task) => {
                                        const range = resolveTaskRange(task);
                                        const isPreviewing =
                                            dragPreview?.taskId === task.id;
                                        // R3 / t17: during a drag we
                                        // override the geometry with
                                        // the live preview dates so
                                        // the bar visibly grows /
                                        // shrinks under the cursor.
                                        const previewStart =
                                            isPreviewing && dragPreview
                                                ? dragPreview.previewStart
                                                : range.start;
                                        const previewEnd =
                                            isPreviewing && dragPreview
                                                ? dragPreview.previewEnd
                                                : range.end;
                                        const previewIsPoint =
                                            previewStart &&
                                            previewEnd &&
                                            previewStart.getTime() === previewEnd.getTime();
                                        const barLeft = offsetForDate(previewStart);
                                        const barWidth = (() => {
                                            if (!previewStart && !previewEnd) return 0;
                                            if (!previewStart || !previewEnd || previewIsPoint) {
                                                return pxPerDay;
                                            }
                                            const days = Math.max(
                                                1,
                                                differenceInCalendarDays(previewEnd, previewStart) + 1
                                            );
                                            return days * pxPerDay;
                                        })();

                                        // R9 / t36: subtask expansion removed — each row is just the
                                        // single GanttRow for that task. No
                                        // childrenByParentId lookup, no
                                        // chevron / isExpanded state on
                                        // GanttRow, no subtask row
                                        // rendering branch.
                                        return (
                                            <GanttRow
                                                key={task.id}
                                                task={task}
                                                barLeft={barLeft}
                                                barWidth={barWidth}
                                                timeScale={timeScale}
                                                groupBy={groupBy}
                                                canDrag={canDrag}
                                                sprints={sprints}
                                                sprintFieldId={sprintField?.id}
                                                customFields={customFields}
                                                onClick={pickState === 'pickingTarget' ? undefined : onTaskClick}
                                                isPreviewing={isPreviewing}
                                                // R4 / t22: dep pick state
                                                onPickDependencyTarget={(direction) =>
                                                    onStartPickTarget(direction)(task.id)
                                                }
                                                isPickSource={pickSourceTaskId === task.id}
                                            />
                                        );
                                    })
                                )}
                            </div>
                        );
                    })}

                    {/* Empty state */}
                    {visibleParentTasks.length === 0 && (
                        <div className="flex items-center justify-center py-12 px-4 text-center">
                            <div>
                                <h3 className="text-base font-medium text-foreground mb-1">
                                    {t('gantt.emptyTitle')}
                                </h3>
                                <p className="text-sm text-foreground-muted max-w-md mx-auto">
                                    {t('gantt.emptyBody')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* t14: dependency-arrow SVG overlay. Absolutely
                        positioned above all rows so the arrows are
                        always on top. Translates 256px to the right to
                        account for the label column (the rows' inner
                        bar geometry is in timeline-local coords). The
                        SVG is sized to totalHeight × totalWidth and
                        pointer-events:none so clicks pass through to
                        the bars underneath. We mark every arrow with
                        a unique marker so the row-id stays reachable
                        for debugging.

                        R11 / t39: the height comes from the same loop
                        that built `rowYByTaskId`, plus one
                        PARENT_ROW_HEIGHT of padding. The padding
                        guards the LAST row's arrow against being
                        clipped at the SVG's bottom edge — the user's
                        reported "arrow not working on the last row"
                        symptom was the LAST row's arrow drawn just
                        below the SVG height because of an off-by-one
                        between the assumed header height and the
                        actual rendered header height. */}
                    {arrows.length > 0 && (() => {
                        const totalHeight = (() => {
                            // Re-derive from the same loop that
                            // populated `rowYByTaskId`. Reading
                            // `rowYByTaskId.size` would tell us only
                            // the unique-row count, so we walk again
                            // — cheap, runs only on dep / row
                            // changes — to keep the SVG height
                            // perfectly aligned with the rows.
                            let h = 40; // header estimate
                            for (const group of groups) {
                                if (group.label) h += GROUP_HEADER_HEIGHT;
                                const groupTasks = visibleParentTasks.filter(
                                    (t) => groupOf(t, groupBy) === group.key
                                );
                                if (groupTasks.length === 0) continue;
                                for (const _task of groupTasks) {
                                    h += PARENT_ROW_HEIGHT;
                                    // R9 / t36: no subtask rows.
                                }
                            }
                            // R11 / t39: pad the SVG by one row
                            // height so the LAST row's arrowhead +
                            // any bottom-border-related geometry is
                            // guaranteed to be inside the SVG.
                            //
                            // R13 / t41: bumped the padding to TWO
                            // row heights. The user's reported
                            // symptom ("last row is bugged in all 4
                            // modes") was reproducible even with the
                            // R11 padding because:
                            //   1. The HEADER_HEIGHT = 40 estimate
                            //      can be a few px short when the
                            //      sticky header wraps to two lines
                            //      (small viewports).
                            //   2. The last-row arrowhead extends
                            //      ~1.5px past `targetY` via the
                            //      marker, and a down-route that
                            //      drops onto the top of the last
                            //      bar can paint another ~4px of
                            //      vertical line below the bar
                            //      before the arrowhead tip.
                            // Two rows of padding (=96px) clears
                            // both safely.
                            h += PARENT_ROW_HEIGHT * 2;
                            return h;
                        })();
                        return (
                            <svg
                                ref={svgRefCallback}
                                className="absolute top-0 left-64 pointer-events-none z-20"
                                width={totalWidth}
                                height={totalHeight}
                                role="presentation"
                                aria-label={t('tasks.dependsOn')}
                            >
                                <defs>
                                    {/* Arrowhead marker. Black, sized
                                        to fit ~6px arrowheads at the
                                        default 1.5px stroke width. */}
                                    <marker
                                        id="gantt-arrow-end"
                                        viewBox="0 0 8 8"
                                        refX="7"
                                        refY="4"
                                        markerWidth="6"
                                        markerHeight="6"
                                        orient="auto-start-reverse"
                                        markerUnits="strokeWidth"
                                    >
                                        <path d="M 0 0 L 8 4 L 0 8 z" fill="currentColor" />
                                    </marker>
                                </defs>
                                {arrows.map((arrow) => {
                                    // R6 / t31: every arrow goes
                                    // LEFT-to-RIGHT. The source anchor is
                                    // already at the right edge of the
                                    // leftmost bar's vertical-middle (Y)
                                    // and the target anchor is at the left
                                    // edge of the rightmost bar's
                                    // vertical-middle. We pull each
                                    // endpoint a few px INSIDE the bar so
                                    // the arrow visibly emerges from
                                    // inside the task at its mid-height.
                                    //
                                    // Path shape — BEHIND route
                                    // (cross-row, right-going, gap ≥ 60px):
                                    //   M (startX, y1)             ← inside pred bar
                                    //   L (startX + stub, y1)      ← exit stub
                                    //   L (startX + stub, y2)      ← descend
                                    //   L (endX, y2)               ← inside succ bar
                                    //
                                    // Same-row case (y1 === y2): single
                                    // straight line.
                                    //
                                    // For the rare left-going case (the
                                    // successor's start is BEFORE the
                                    // predecessor's end), we keep the
                                    // stub pattern but bump the start
                                    // offset to 32px so the line still
                                    // clears the bars. All such edges
                                    // are normalised in the arrows
                                    // useMemo above.
                                    //
                                    // R7 / t33: ABOVE route (gap < 60px,
                                    // bars overlapping or stacked). The
                                    // arc exits the source bar, climbs
                                    // above both bars (24px above the
                                    // source row's vertical-middle),
                                    // travels horizontally past the
                                    // target column, and drops back to
                                    // the target's left edge. The arc Y
                                    // is clamped so it never escapes the
                                    // SVG height — if the source sits
                                    // near the top of the timeline we
                                    // collapse to a flat horizontal stub
                                    // instead of going negative.
                                    const STUB_PX = 8;
                                    const INSET_PX = 4;
                                    // R13 / t41: the stub exits the source
                                    // bar going AWAY from it (i.e. toward
                                    // the target). When the target sits to
                                    // the RIGHT, the stub is `+STUB_PX` to
                                    // the right of startX. When the target
                                    // sits to the LEFT (rare — successor
                                    // scheduled before its predecessor), the
                                    // stub is `-STUB_PX` to the left of
                                    // startX so the polyline exits the
                                    // source bar toward the target instead
                                    // of doubling back the wrong way.
                                    const midX = arrow.x1 + (arrow.goingRight ? STUB_PX : -STUB_PX);
                                    // R6: insets go inwards relative to
                                    // the direction of travel so the
                                    // arrow always lands INSIDE the bar.
                                    // R9 / t36: the above-route no longer
                                    // insets the end (it lands above the
                                    // target rather than inside it).
                                    //
                                    // R13 / t41: the start inset is ALWAYS
                                    // `-INSET_PX` (i.e. 4px inside the
                                    // source bar's right edge) regardless
                                    // of direction. With the swap in the
                                    // arrows useMemo, the source is now
                                    // always the predecessor, so we never
                                    // need a "going-left" start offset.
                                    const startInset = -INSET_PX;
                                    // R13 / t41: the end inset is ALWAYS
                                    // `-2px` so the marker (which extends
                                    // ~1.5px past the path end via
                                    // refX=7 + markerUnits=strokeWidth)
                                    // lands just OUTSIDE the target bar's
                                    // left edge instead of overlapping the
                                    // bar's body. The 'above' route still
                                    // keeps `0` (it lands on the target
                                    // top, not the left edge) and the
                                    // 'sameRow' line keeps `0` (straight
                                    // line, no end geometry).
                                    const endInset = arrow.route === 'down'
                                        ? -2
                                        : 0;
                                    const startX = arrow.x1 + startInset;
                                    const endX = arrow.x2 + endInset;
                                    let path: string;
                                    if (arrow.route === 'above') {
                                        // R10 / t38: 'above' is only used
                                        // when the source is BELOW the
                                        // target (sourceY > targetY), so
                                        // the arc climbs from the lower
                                        // source up over both bars and
                                        // drops onto the top of the target.
                                        // Clamp the arc Y at ≥ 6 so it
                                        // never escapes the SVG when the
                                        // source sits on the very first
                                        // row.
                                        //
                                        // R14 / t44: the arc now drops to
                                        // `arrow.y2` (= target.middleY,
                                        // the target bar's vertical
                                        // middle) instead of
                                        // `targetTopY - 4` (4px above
                                        // the bar's top). The previous
                                        // behaviour put the arrowhead
                                        // at the TOP-LEFT of the target
                                        // bar — visibly inconsistent
                                        // with the 'down' route which
                                        // lands at the LEFT-MIDDLE. The
                                        // user wanted a single,
                                        // predictable endpoint
                                        // regardless of direction. The
                                        // longer vertical drop (from
                                        // arcY down to the target's
                                        // middle) crosses the target
                                        // bar's top half — acceptable
                                        // because the arrow always ends
                                        // outside the bar (endInset =
                                        // -2 puts the marker tip 0.5px
                                        // outside the left edge).
                                        const ARC_CLEARANCE = 24;
                                        const arcY = Math.max(
                                            6,
                                            arrow.sourceTopY - ARC_CLEARANCE
                                        );
                                        path = `M ${startX} ${arrow.y1} ` +
                                               `L ${startX} ${arcY} ` +
                                               `L ${endX} ${arcY} ` +
                                               `L ${endX} ${arrow.y2}`;
                                    } else if (arrow.route === 'sameRow') {
                                        path = `M ${startX} ${arrow.y1} L ${endX} ${arrow.y2}`;
                                    } else {
                                        // 'down' — simple 90° polyline
                                        // from the source's right-middle,
                                        // down to the target row Y, then
                                        // horizontally into the target's
                                        // left edge. No arc.
                                        path = `M ${startX} ${arrow.y1} ` +
                                               `L ${midX} ${arrow.y1} ` +
                                               `L ${midX} ${arrow.y2} ` +
                                               `L ${endX} ${arrow.y2}`;
                                    }
                                    // R6: all arrows are the same indigo
                                    // solid line (FS) — no more type-based
                                    // colour/dash differentiation.
                                    return (
                                        <path
                                            key={arrow.id}
                                            d={path}
                                            fill="none"
                                            stroke="#6366f1"
                                            strokeWidth={1.5}
                                            strokeLinejoin="miter"
                                            markerEnd="url(#gantt-arrow-end)"
                                            style={{ color: '#6366f1' }}
                                            data-dep-id={arrow.id}
                                            data-dep-type="FS"
                                            data-arrow-shape="left-to-right"
                                            data-arrow-route={arrow.route}
                                            data-arrow-anchor="bar-vertical-middle"
                                        />
                                    );
                                })}
                            </svg>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}
